import { ApiError, diagnosticCause } from './appError.ts';

const DEFAULT_DEDUPE_WINDOW_MS = 5 * 60 * 1000;
const DEFAULT_MAX_SIGNATURES = 200;

const diagnosticSignature = (err: unknown, section?: string): string => {
  const cause = diagnosticCause(err);
  const causeName = cause instanceof Error ? cause.name : typeof cause;
  const causeMessage = cause instanceof Error ? cause.message : String(cause);
  const apiDetails = err instanceof ApiError
    ? `${err.status ?? 'none'}:${err.code ?? 'none'}:${err.message}`
    : 'not-api';

  return `${section || 'unscoped'}|${apiDetails}|${causeName}:${causeMessage}`;
};

/**
 * Suppresses repeated reports from render loops and polling outages while
 * allowing the same diagnostic to be reported again after a cooling window.
 */
export class ErrorReportDedupe {
  private readonly reportedObjects = new WeakSet<object>();
  private readonly recentSignatures = new Map<string, number>();
  private readonly windowMs: number;
  private readonly maxSignatures: number;

  constructor(
    windowMs = DEFAULT_DEDUPE_WINDOW_MS,
    maxSignatures = DEFAULT_MAX_SIGNATURES,
  ) {
    this.windowMs = windowMs;
    this.maxSignatures = maxSignatures;
  }

  shouldReport(err: unknown, section?: string, nowMs = Date.now()): boolean {
    if (err == null) return false;

    if (typeof err === 'object') {
      if (this.reportedObjects.has(err)) return false;
      this.reportedObjects.add(err);
    }

    this.prune(nowMs);
    const signature = diagnosticSignature(err, section);
    const lastReportedAt = this.recentSignatures.get(signature);
    if (
      lastReportedAt !== undefined
      && nowMs - lastReportedAt < this.windowMs
    ) {
      return false;
    }

    // Refresh insertion order so the bounded map evicts the oldest report.
    this.recentSignatures.delete(signature);
    this.recentSignatures.set(signature, nowMs);
    while (this.recentSignatures.size > this.maxSignatures) {
      const oldestSignature = this.recentSignatures.keys().next().value;
      if (oldestSignature === undefined) break;
      this.recentSignatures.delete(oldestSignature);
    }
    return true;
  }

  private prune(nowMs: number): void {
    for (const [signature, reportedAt] of this.recentSignatures) {
      if (nowMs - reportedAt >= this.windowMs) {
        this.recentSignatures.delete(signature);
      }
    }
  }
}
